import { type ReactNode } from 'react';
import { useAuthStore } from '@/features/auth/hooks/useAuth';
import type { ModuleKey } from '@/lib/modules';

export interface ModuleGuardProps {
  /** The module this route belongs to. */
  module: ModuleKey;
  /** Content rendered when the module is enabled (or not yet loaded). */
  children: ReactNode;
  /** Custom fallback shown when the module is disabled. */
  fallback?: ReactNode;
}

/**
 * Route guard that hides a module-backed page when the tenant has the module
 * disabled. Defense-in-depth only — the API gate (`tenantModule`) is the hard
 * stop; this guard just avoids rendering a page that would 403 on every call.
 *
 * While `enabledModules` is `null` (not yet hydrated from /auth/me) children
 * render — the server still enforces access, and this avoids a flash of the
 * fallback during initialization. Once a loaded array omits the module key, the
 * fallback is shown.
 */
export function ModuleGuard({ module, children, fallback }: ModuleGuardProps) {
  const enabledModules = useAuthStore((s) => s.enabledModules);

  if (enabledModules !== null && !enabledModules.includes(module)) {
    if (fallback) return <>{fallback}</>;
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Module not available
        </h1>
        <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
          This module is not enabled for your organization. Contact your administrator if you need
          access.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
